import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';

@Component({
  selector: 'app-custom-editor',
  templateUrl: './custom-editor.component.html',
  styleUrls: ['./custom-editor.component.scss']
})
export class CustomEditorComponent implements AfterViewInit {
  @ViewChild('editor') editorRef!: ElementRef<HTMLDivElement>;
  currentPage = 1;

  ngAfterViewInit() {
    // seed editor with a paragraph so typing is comfortable
    const el = this.editorRef.nativeElement;
    if (!el.innerHTML.trim()) {
      el.innerHTML = '<p><br></p>';
    }
  }

  // ---------- Selection helpers ----------
  private getSelectionRange(): Range | null {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    return sel.getRangeAt(0);
  }

  private wrapSelectionWith(tag: string, style?: string) {
    const range = this.getSelectionRange();
    if (!range) return;
    if (range.collapsed) {
      // insert empty element and place caret inside
      const el = document.createElement(tag);
      if (style) el.setAttribute('style', style);
      el.appendChild(document.createTextNode('\u200B')); // zero-width placeholder
      range.insertNode(el);
      // move caret inside the inserted node
      range.setStart(el.firstChild!, 1);
      range.setEnd(el.firstChild!, 1);
      const sel = window.getSelection();
      sel!.removeAllRanges();
      sel!.addRange(range);
    } else {
      // surround contents (try/catch because surroundContents can fail)
      try {
        const wrapper = document.createElement(tag);
        if (style) wrapper.setAttribute('style', style);
        wrapper.appendChild(range.extractContents());
        range.insertNode(wrapper);
      } catch (err) {
        // fallback: create span and append HTML
        const wrapper = document.createElement('span');
        if (style) wrapper.setAttribute('style', style);
        wrapper.innerHTML = range.toString();
        range.deleteContents();
        range.insertNode(wrapper);
      }
    }
    this.updatePageNumber();
  }

  // ---------- Toolbar actions ----------
  toggleInline(cmd: 'bold' | 'italic' | 'strike') {
    // toggle by checking if selection already has tag
    const range = this.getSelectionRange();
    if (!range) return;

    // We'll use tags: bold -> <strong>, italic -> <em>, strike -> <s>
    if (cmd === 'bold') {
      this.toggleWrapTag('strong');
    } else if (cmd === 'italic') {
      this.toggleWrapTag('em');
    } else if (cmd === 'strike') {
      this.toggleWrapTag('s');
    }
  }

  private toggleWrapTag(tagName: string) {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (range.collapsed) {
      // If caret is inside an existing tag of this type, remove it for simplicity.
      const node = sel.anchorNode as Node;
      const parent = this.findClosestAncestor(node, tagName);
      if (parent) {
        // unwrap the element
        this.unwrapElement(parent);
      } else {
        this.wrapSelectionWith(tagName);
      }
    } else {
      // If entire selection already wrapped by same tag, unwrap; else wrap
      const container = range.commonAncestorContainer;
      const existing = this.findClosestAncestor(container, tagName);
      if (existing && existing.contains(range.startContainer) && existing.contains(range.endContainer)) {
        this.unwrapElement(existing);
      } else {
        this.wrapSelectionWith(tagName);
      }
    }
    this.updatePageNumber();
  }

  private findClosestAncestor(node: Node | null, tagName: string): HTMLElement | null {
    while (node && node !== this.editorRef.nativeElement) {
      if (node instanceof HTMLElement && node.tagName.toLowerCase() === tagName.toLowerCase()) {
        return node;
      }
      node = node.parentNode;
    }
    return null;
  }

  private unwrapElement(el: HTMLElement) {
    const parent = el.parentNode!;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  }

  applyColor(event: Event, background = false) {
    const input = event.target as HTMLInputElement;
    const color = input.value;
    const style = background ? `background-color: ${color};` : `color: ${color};`;
    // apply as span with style
    this.wrapSelectionWith('span', style);
  }

  applyFontSize(event: Event) {
    const sel = (event.target as HTMLSelectElement).value;
    if (!sel) return;
    const style = `font-size: ${sel};`;
    this.wrapSelectionWith('span', style);
  }

  // ---------- Insert Table ----------
  insertTablePrompt() {
    const rowsStr = prompt('Rows', '2');
    const colsStr = prompt('Columns', '2');
    if (!rowsStr || !colsStr) return;
    const rows = Math.max(1, parseInt(rowsStr, 10) || 1);
    const cols = Math.max(1, parseInt(colsStr, 10) || 1);
    this.insertTable(rows, cols);
  }

  insertTable(rows: number, cols: number) {
    const table = document.createElement('table');
    table.setAttribute('border', '1');
    table.style.borderCollapse = 'collapse';
    table.style.width = '100%';
    for (let r = 0; r < rows; r++) {
      const tr = document.createElement('tr');
      for (let c = 0; c < cols; c++) {
        const td = document.createElement('td');
        td.style.padding = '6px';
        td.innerHTML = '&nbsp;'; // placeholder
        tr.appendChild(td);
      }
      table.appendChild(tr);
    }
    // Insert at caret
    const range = this.getSelectionRange();
    if (!range) {
      this.editorRef.nativeElement.appendChild(table);
      return;
    }
    range.deleteContents();
    range.insertNode(table);
    // add a paragraph after table for continued typing
    const p = document.createElement('p');
    p.innerHTML = '<br>';
    table.parentNode!.insertBefore(p, table.nextSibling);
    // move caret into the first cell
    const firstCell = table.querySelector('td')!;
    const sel = window.getSelection()!;
    const newRange = document.createRange();
    newRange.setStart(firstCell, 0);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);
    this.updatePageNumber();
  }

  // ---------- Page break ----------
  insertPageBreak() {
    const range = this.getSelectionRange();
    const br = document.createElement('div');
    br.className = 'page-break';
    br.setAttribute('contenteditable', 'false'); // so it doesn't get typed into
    br.innerHTML = '<div class="page-break-line"></div>';
    if (!range) {
      this.editorRef.nativeElement.appendChild(br);
    } else {
      // ensure we are inserting between blocks: split if inside text node
      range.collapse(false);
      range.insertNode(br);
      // insert a paragraph after break so user can continue typing
      const p = document.createElement('p');
      p.innerHTML = '<br>';
      br.parentNode!.insertBefore(p, br.nextSibling);
    }
    // move caret into next paragraph
    const sel = window.getSelection()!;
    const next = br.nextSibling as Node;
    if (next) {
      const newRange = document.createRange();
      // ensure next is text container
      if (next.nodeType === Node.TEXT_NODE) {
        newRange.setStart(next, 0);
      } else {
        // prefer first child or just after node
        const child = (next as Element).firstChild || next;
        newRange.setStart(child, 0);
      }
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
    }
    this.updatePageNumber();
  }

  // ---------- Auto lists (on typing) ----------
  onKeyDown(evt: KeyboardEvent) {
    // We want to intercept Space or Enter when user typed a pattern at line start
    if (evt.key !== ' ' && evt.key !== 'Enter') return;

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    // find the block container (paragraph or div or li)
    let block = range.startContainer;
    while (block && block !== this.editorRef.nativeElement && block.nodeType !== Node.ELEMENT_NODE) {
      block = block.parentNode!;
    }
    if (!block || block === this.editorRef.nativeElement) {
      // try getting the immediate paragraph node where caret is
      block = range.startContainer;
      while (block && block.parentNode && block.parentNode !== this.editorRef.nativeElement) {
        block = block.parentNode;
      }
    }
    if (!block) return;

    // get the full text content of line/block up to caret
    const caretOffset = range.startOffset;
    let textBefore = '';
    if (range.startContainer.nodeType === Node.TEXT_NODE) {
      textBefore = (range.startContainer as Text).data.slice(0, caretOffset);
    } else {
      // fallback: get block text
      textBefore = (block as Element).textContent || '';
    }

    // trim leading whitespace
    const trimmed = textBefore.replace(/^\s+/, '');

    // pattern: 1. or 1) at line start -> ordered list
    const orderedMatch = trimmed.match(/^(\d+)[\.\)]$/);
    const bulletMatch = trimmed.match(/^[-\*]$/);

    if (orderedMatch) {
      evt.preventDefault();
      // replace block with an <ol><li>content</li></ol>
      this.convertBlockToList(block as Node, true);
      return;
    }

    if (bulletMatch) {
      evt.preventDefault();
      this.convertBlockToList(block as Node, false);
      return;
    }
  }

  private convertBlockToList(blockNode: Node, ordered: boolean) {
    // blockNode might be a text node or <p>
    // Extract its text, remove the initial marker, and create list
    const blockEl = (blockNode.nodeType === Node.TEXT_NODE) ? blockNode.parentElement : (blockNode as Element);
    if (!blockEl) return;
    const fullText = blockEl.textContent || '';
    // remove marker (like "1." or "-" etc)
    const newText = fullText.replace(/^\s*(\d+[\.\)]|[-\*])\s*/, '');
    const list = document.createElement(ordered ? 'ol' : 'ul');
    const li = document.createElement('li');
    li.textContent = newText || '\u00A0';
    list.appendChild(li);
    // replace the block element
    blockEl.parentNode!.insertBefore(list, blockEl);
    blockEl.parentNode!.removeChild(blockEl);

    // place caret inside new li at end
    const sel = window.getSelection()!;
    const r = document.createRange();
    r.selectNodeContents(li);
    r.collapse(false);
    sel.removeAllRanges();
    sel.addRange(r);

    this.updatePageNumber();
  }

  // ---------- Input & page number calculation ----------
  onInput(_evt: Event) {
    // ensure there is always at least one paragraph
    const el = this.editorRef.nativeElement;
    if (!el.innerHTML.trim()) {
      el.innerHTML = '<p><br></p>';
    }
    this.updatePageNumber();
  }

  // simple page number: count how many .page-break elements are before caret
  updatePageNumber() {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) {
      // fallback: total pages = number of breaks + 1
      const total = this.editorRef.nativeElement.querySelectorAll('.page-break').length + 1;
      this.currentPage = total;
      return;
    }
    const range = sel.getRangeAt(0);
    // find node containing caret
    let node: Node | null = range.startContainer;
    // walk upward until direct child of editor
    while (node && node !== this.editorRef.nativeElement) {
      if (node.parentNode === this.editorRef.nativeElement) break;
      node = node.parentNode;
    }
    // Count page-breaks before this node
    const children = Array.from(this.editorRef.nativeElement.childNodes);
    let page = 1;
    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE && (child as Element).classList.contains('page-break')) {
        page++;
      }
      if (child === node) break;
      // if node is inside child, also break
      if (node && child.contains && (child as Node).contains(node)) break;
    }
    this.currentPage = page;
  }
}